import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../../components/common/DashboardLayout';
import AdminNav from '../../components/admin/AdminNav';
import RichTextEditor from '../../components/common/RichTextEditor';
import * as crService from '../../services/adminAffiliateControlRoom.service';
import * as affiliateAdminService from '../../services/adminAffiliate.service';
import { uploadImage } from '../../services/upload.service';
import AffiliateWaitingBox from '../../components/admin/AffiliateWaitingBox';
import PartnersView from '../../components/admin/PartnersView';

/**
 * Affiliate & Partner Control Room — built strictly from CB's approved
 * mockup (365reset-affiliate-admin-mockup.html), Jenn's explicit
 * instruction 2026-09-12: "stay strictly to it. No mavericking. For
 * anything we haven't built the tracking/functionality for: create a
 * to-do for that and a placeholder into the control room simply as In
 * Development." Two sections are real "In Development" placeholders for
 * that reason, not filled with invented numbers: the pipeline funnel/
 * channel breakdown (needs click/view/checkout-start tracking that
 * doesn't exist) and the Content Library (needs multi-template
 * assignment + admin-editable per-platform captions, both genuinely
 * unbuilt). Everything else here is real data, verified against
 * production before this was written.
 *
 * One deliberate departure from the mockup's raw HTML: the mockup uses
 * <h1> for the affiliate name in the detail pane. This app's own hard
 * rule (CLAUDE.md) reserves <h1> for the page's own single title — kept
 * here as a styled <h2> instead so the visual size/weight matches the
 * mockup exactly, just not the semantic tag.
 */

const HEADER_STYLES = [
  { key: 'jenn_photo', label: "Hey, it's Jenn" },
  { key: 'compass', label: 'Compass' },
];

// The real funnel shape from the mockup (4 trapezoid segments), always
// rendered. 2026-09-28: all four steps are real now — the first three come
// from our own first-party tracking (marketing_events, this month, each
// person counted once). If the numbers ever fail to arrive, a segment falls
// back to "In development" rather than showing a made-up count.
function FunnelVertical({ joinedThisMonth, pipeline }) {
  const segments = [
    { clip: 'polygon(0 0, 100% 0, 91% 100%, 9% 100%)', bg: '#F0EBE3', text: 'text-reset-brownDeep', label: 'Clicked an affiliate link', value: pipeline?.clicked },
    { clip: 'polygon(9% 0, 91% 0, 82% 100%, 18% 100%)', bg: '#e4c9b4', text: 'text-reset-brownDeep', label: 'Viewed a landing page', value: pipeline?.viewedLanding },
    { clip: 'polygon(18% 0, 82% 0, 73% 100%, 27% 100%)', bg: '#C4714A', text: 'text-reset-cream', label: 'Started signing up', value: pipeline?.startedSignup },
  ];
  return (
    <div className="flex flex-col w-80 shrink-0">
      {segments.map((s, i) => (
        <div key={s.label} className={`text-center px-5 pt-5 pb-4 ${s.text}`} style={{ background: s.bg, clipPath: s.clip, marginTop: i === 0 ? 0 : -1 }}>
          {s.value != null
            ? <p className="font-serif text-2xl leading-none">{s.value}</p>
            : <p className="text-xs font-bold uppercase tracking-wide opacity-70">In development</p>}
          <p className="text-xs mt-0.5 opacity-80">{s.label}</p>
        </div>
      ))}
      <div className="text-center px-5 pt-5 pb-6 text-reset-cream" style={{ background: '#4A6355', clipPath: 'polygon(27% 0, 73% 0, 65% 100%, 35% 100%)', marginTop: -1 }}>
        <p className="font-serif text-2xl leading-none">{joinedThisMonth}</p>
        <p className="text-xs mt-0.5 text-reset-cream/80">Joined this month</p>
      </div>
    </div>
  );
}

function InDevelopmentCard({ title, sub, note }) {
  return (
    <div className="bg-reset-creamDark border-2 border-dashed border-reset-terracotta/30 rounded-2xl p-6 text-center">
      <p className="text-xs font-bold uppercase tracking-wide text-reset-terracotta mb-1">In development</p>
      <h3 className="font-serif text-lg text-reset-brownDeep mb-1">{title}</h3>
      {sub && <p className="text-sm text-gray-500 max-w-md mx-auto">{sub}</p>}
      {note && <p className="text-xs text-gray-400 mt-3 max-w-md mx-auto">{note}</p>}
    </div>
  );
}

export default function AdminAffiliateControlRoom() {
  const [view, setView] = useState(window.location.hash === '#partners' ? 'partners' : 'overview'); // #partners: back from a partner's page

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto">
        <AdminNav />
        {/* Jenn, 2026-09-12: "The Top being on the beige doesn't anchor the
            view" — this is the mockup's own dark header.top bar (wordmark +
            view switcher), not the plain white tab row that was here before.
            font-serif is kept to this page's one real <h1>, inside this bar,
            matching brand rule. */}
        <div className="bg-reset-brownDeep rounded-t-2xl overflow-hidden -mx-px">
          <div className="flex items-center justify-between px-6 py-3.5">
            <h1 className="font-serif text-lg text-reset-cream">
              <span className="text-reset-gold">365reset</span> · Affiliate Manager
            </h1>
            <span className="text-xs text-reset-cream/60">Logged in as Jenn</span>
          </div>
          <div className="flex gap-1 px-5 flex-wrap">
            {[
              { key: 'overview', label: 'Overview' },
              { key: 'affiliates', label: 'Affiliates' },
              { key: 'partners', label: 'Partners' },
            ].map(t => (
              <button
                key={t.key}
                onClick={() => setView(t.key)}
                className={`px-4 py-3 text-sm font-bold border-b-[3px] transition-colors ${
                  view === t.key ? 'border-reset-terracotta text-reset-cream' : 'border-transparent text-reset-cream/60 hover:text-reset-cream'
                }`}
              >
                {t.label}
              </button>
            ))}
            <button
              onClick={() => setView('library')}
              className={`px-4 py-3 text-sm font-bold border-b-[3px] transition-colors ${
                view === 'library' ? 'border-reset-terracotta text-reset-cream' : 'border-transparent text-reset-cream/60 hover:text-reset-cream'
              }`}
            >
              Content Library
            </button>
          </div>
        </div>

        <div className="pt-8">
          <AffiliateWaitingBox />
          {view === 'overview' && <OverviewView onManageAffiliates={() => setView('affiliates')} onManagePartners={() => setView('partners')} />}
          {view === 'partners' && <PartnersView />}
          {view === 'affiliates' && <AffiliatesView />}
          {view === 'library' && <LibraryView />}
        </div>
      </div>
    </DashboardLayout>
  );
}

// ═══════════════════════ OVERVIEW ═══════════════════════

function KpiCircle({ value, label }) {
  return (
    <div className="w-28 h-28 rounded-full bg-white border border-reset-creamDark flex flex-col items-center justify-center text-center p-2 shrink-0">
      <div className="font-serif text-2xl text-reset-terracotta leading-none">{value}</div>
      <div className="text-[10px] font-bold text-reset-sage mt-1 leading-tight">{label}</div>
    </div>
  );
}

function OverviewView({ onManageAffiliates, onManagePartners }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [segmentCounts, setSegmentCounts] = useState(null);
  const [recipientSegment, setRecipientSegment] = useState('all');
  const [compose, setCompose] = useState(null); // {type, title, subject, body}
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    crService.getOverview().then(setData).catch(err => setError(err.response?.data?.error || 'Could not load the overview.'));
    crService.getSegmentCounts().then(setSegmentCounts).catch(() => {});
  }, []);

  const composeTemplates = {
    custom: { title: 'Send a custom email', subject: '', body: '' },
    intake: {
      title: 'Send intake email',
      subject: "Let's get your affiliate profile set up",
      body: "Hi — to get your link and landing page live, we just need a few things from you: a photo, a short story of why you joined 365reset, who you'd send it to, and a quote we can feature. Use the link in this email to fill out the form.",
    },
    landing_refresh: {
      title: 'Request new landing pages',
      subject: 'Time for a fresh 365reset landing page',
      body: "Hi — we're refreshing affiliate landing pages. When you have a few minutes, please fill out the form in this email with an updated photo, story, and quote, and we'll have your new page live shortly.",
    },
  };

  const openCompose = (type) => {
    const t = composeTemplates[type];
    setCompose({ type, ...t, headerStyle: 'jenn_photo' });
    setResult(null);
  };

  const handleSend = async () => {
    setSending(true);
    setResult(null);
    try {
      const res = await crService.bulkEmail({ segment: recipientSegment, type: compose.type, subject: compose.subject, body: compose.body, headerStyle: compose.headerStyle });
      setResult({ ok: true, message: `Sent to ${res.sent} of ${res.total}${res.failed.length ? ` (${res.failed.length} failed: ${res.failed.join(', ')})` : ''}.` });
      setCompose(null);
    } catch (err) {
      setResult({ ok: false, message: err.response?.data?.error || 'Could not send this.' });
    } finally {
      setSending(false);
    }
  };

  if (error) return <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm">{error}</div>;
  if (!data) return <p className="text-sm text-gray-400">Loading…</p>;

  const segmentLabel = { all: `All affiliates (${segmentCounts?.all ?? '…'})`, live: `Live affiliates only (${segmentCounts?.live ?? '…'})`, pending: `Pending / incomplete (${segmentCounts?.pending ?? '…'})` };

  return (
    <div>
      {/* KPI strip */}
      <div className="flex items-stretch gap-6 flex-wrap mb-10">
        <KpiCircle value={data.membersThisMonth} label="Members this month" />
        <KpiCircle value={`$${Number(data.revenueMtd).toFixed(0)}`} label="Revenue, MTD" />
        <KpiCircle value={<>{data.activeAffiliates}<span className="text-sm text-gray-400"> /{data.totalAffiliates}</span></>} label="Active affiliates" />
        <div className="bg-white border border-reset-creamDark rounded-2xl px-6 py-4 border-l-[5px] border-l-reset-sage w-[200px] shrink-0 md:ml-auto flex flex-col justify-center">
          <p className="text-xs font-bold text-reset-sage mb-1">Next payout</p>
          <p className="font-serif text-xl text-reset-brownDeep">{new Date(data.nextPayout.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} · ${Number(data.nextPayout.amount).toFixed(0)}</p>
          <p className="text-xs text-gray-500 mt-1">Going out to {data.nextPayout.peopleCount} affiliate{data.nextPayout.peopleCount === 1 ? '' : 's'}</p>
        </div>
      </div>

      {/* Pipeline — the real funnel shape always renders (the vessel).
          Every step is real as of 2026-09-28 (first-party tracking);
          "Joined this month" is the same number as the KPI circle above. */}
      <section className="mb-10">
        <h2 className="font-serif text-xl text-reset-brownDeep mb-1">What's coming through the pipeline</h2>
        <p className="text-sm text-gray-500 mb-4">Combined across every affiliate — this is your forecast</p>
        <div className="flex items-center gap-8 flex-wrap justify-center bg-white border border-reset-creamDark rounded-2xl py-8 px-6">
          <FunnelVertical joinedThisMonth={data.membersThisMonth} pipeline={data.pipeline} />
          <div className="w-[190px] h-[190px] rounded-full bg-reset-gold/35 flex flex-col items-center justify-center text-center p-4 shrink-0">
            <p className="text-xs font-bold uppercase tracking-wide text-reset-terracotta mb-1">In development</p>
            <p className="text-sm text-reset-brown">Forecasting needs a few months of the numbers alongside.</p>
          </div>
        </div>
      </section>

      <div className="grid md:grid-cols-2 gap-8 mb-10">
        <section>
          <h2 className="font-serif text-xl text-reset-brownDeep mb-1">Top performers</h2>
          <p className="text-sm text-gray-500 mb-3">Ranked by members referred this month</p>
          <div className="bg-white border border-reset-creamDark rounded-2xl px-5 py-2">
            {data.topPerformers.length === 0 ? (
              <p className="text-sm text-gray-400 py-4">No affiliates yet.</p>
            ) : data.topPerformers.slice(0, 5).map((p, i) => {
              const max = Math.max(...data.topPerformers.map(x => x.referrals_this_month), 1);
              return (
                <div key={p.member_id} className="py-3.5 border-b border-reset-creamDark last:border-0">
                  <div className="flex justify-between items-baseline mb-1.5">
                    <span className="text-sm font-bold text-reset-brownDeep">
                      {p.name}
                      <span className="font-normal text-xs text-gray-400 ml-2">
                        {p.tags.includes('royalty') && 'Royalty + Affiliate · '}
                        {p.tags.includes('live') && 'In development'}
                        {p.tags.includes('pending') && 'Pending — landing page not live yet'}
                        {p.tags.includes('incomplete') && 'Incomplete profile'}
                      </span>
                    </span>
                    <span className="font-serif text-lg text-reset-terracotta">{p.referrals_this_month}</span>
                  </div>
                  <div className="bg-reset-creamDark rounded-full h-2.5 overflow-hidden">
                    <div className={`h-full rounded-full ${i === 0 && p.referrals_this_month > 0 ? 'bg-reset-gold' : 'bg-reset-terracotta'}`} style={{ width: `${Math.max(2, (p.referrals_this_month / max) * 100)}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <section>
          <h2 className="font-serif text-xl text-reset-brownDeep mb-1">Where it's coming from</h2>
          <p className="text-sm text-gray-500 mb-3">Share of joins by channel, this month</p>
          <div className="bg-white border border-reset-creamDark rounded-2xl px-5 py-2">
            {/* 2026-09-28: real — each of this month's affiliate joins placed
                by the tagged link or referring site saved at signup
                (marketingEvent.affiliateJoinsByChannelThisMonth). Joins with
                no source at all are counted below the list, never guessed. */}
            {(data.channels || ['Instagram & Threads', 'Email, direct from affiliates', 'Newsletter inserts', 'Facebook', 'Pinterest & other'].map(channel => ({ channel, pct: null })))
              .map(({ channel, pct }) => (
              <div key={channel} className="py-3 border-b border-reset-creamDark last:border-0">
                <div className="flex justify-between text-sm mb-1.5">
                  <span className="font-bold text-reset-brown">{channel}</span>
                  {pct == null
                    ? <span className="text-reset-terracotta font-bold text-xs">In development</span>
                    : <span className="text-reset-terracotta font-bold text-xs">{pct}%</span>}
                </div>
                <div className="bg-reset-creamDark rounded-full h-2 overflow-hidden">
                  {pct > 0 && <div className="h-full rounded-full bg-reset-terracotta" style={{ width: `${pct}%` }} />}
                </div>
              </div>
            ))}
          </div>
          {data.channelsUnknown > 0 && (
            <p className="text-xs text-gray-500 mt-2">
              {data.channelsUnknown} {data.channelsUnknown === 1 ? 'join' : 'joins'} this month came with no link tag or referring site, so {data.channelsUnknown === 1 ? "it isn't" : "they aren't"} counted above.
            </p>
          )}
        </section>
      </div>

      <section className="mb-10">
        <h2 className="font-serif text-xl text-reset-brownDeep mb-1">Communicate with your affiliates</h2>
        <p className="text-sm text-gray-500 mb-4">For launching a campaign or reaching more than one at once</p>
        <div className="bg-white border border-reset-creamDark rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <span className="text-sm text-gray-500">Send to:</span>
            <select value={recipientSegment} onChange={e => setRecipientSegment(e.target.value)} className="border border-gray-200 rounded-full px-4 py-2 text-sm">
              <option value="all">{segmentLabel.all}</option>
              <option value="live">{segmentLabel.live}</option>
              <option value="pending">{segmentLabel.pending}</option>
            </select>
          </div>
          {result && (
            <div className={`text-sm px-3 py-2 rounded-lg mb-4 ${result.ok ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>{result.message}</div>
          )}
          <div className="flex gap-3 flex-wrap">
            <button onClick={() => openCompose('custom')} className="px-4 py-2.5 bg-reset-terracotta text-white text-sm font-bold rounded-full">Custom email</button>
            <button onClick={() => openCompose('intake')} className="px-4 py-2.5 border-[1.5px] border-reset-brownDeep/25 text-reset-brownDeep text-sm font-bold rounded-full">Pre-formatted intake email</button>
            <button onClick={() => openCompose('landing_refresh')} className="px-4 py-2.5 border-[1.5px] border-reset-brownDeep/25 text-reset-brownDeep text-sm font-bold rounded-full">Request new landing pages</button>
          </div>
        </div>
      </section>

      <section>
        <h2 className="font-serif text-xl text-reset-brownDeep mb-4">Your sales team, by program</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="bg-white border border-reset-creamDark rounded-2xl p-6">
            <span className="inline-block text-xs font-bold px-2.5 py-1 rounded-full bg-reset-sage/10 text-reset-sage mb-2">Live</span>
            <h3 className="font-serif text-xl text-reset-brownDeep mb-1">Affiliates</h3>
            <p className="text-sm text-gray-500 mb-3">Members who share 365reset with their own audience. {data.totalAffiliates} total, {data.activeAffiliates} active and earning.</p>
            <button onClick={onManageAffiliates} className="text-sm font-bold text-reset-terracotta">Manage affiliates →</button>
          </div>
          <div className="bg-white border border-reset-creamDark rounded-2xl p-6">
            <span className="inline-block text-xs font-bold px-2.5 py-1 rounded-full bg-reset-sage/10 text-reset-sage mb-2">Live</span>
            <h3 className="font-serif text-xl text-reset-brownDeep mb-1">Partners</h3>
            <p className="text-sm text-gray-500 mb-3">Coaches and therapists referring clients under a branded partner link — kept as its own program so it's never confused with affiliates.</p>
            <button onClick={onManagePartners} className="text-sm font-bold text-reset-terracotta">Manage partners →</button>
          </div>
        </div>
      </section>

      {compose && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={() => setCompose(null)}>
          <div className="bg-white rounded-2xl shadow-lg max-w-lg w-full p-6" onClick={e => e.stopPropagation()}>
            <h3 className="font-serif text-lg text-reset-brownDeep mb-4">{compose.title}</h3>
            <div className="mb-3">
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">Banner</label>
              <div className="flex gap-2">
                {HEADER_STYLES.map(hs => (
                  <button key={hs.key} onClick={() => setCompose({ ...compose, headerStyle: hs.key })}
                    className={`px-3 py-1.5 rounded-lg border text-xs font-medium ${compose.headerStyle === hs.key ? 'border-reset-terracotta bg-reset-terracotta/5' : 'border-gray-200'}`}>
                    {hs.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="mb-3">
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">To</label>
              <input readOnly value={segmentLabel[recipientSegment]} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-gray-50" />
            </div>
            <div className="mb-3">
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">Subject</label>
              <input value={compose.subject} onChange={e => setCompose({ ...compose, subject: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div className="mb-4">
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">Message</label>
              <RichTextEditor value={compose.body} onChange={(body) => setCompose({ ...compose, body })} minHeightClass="min-h-[160px]" />
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => setCompose(null)} className="text-sm text-gray-500">Cancel</button>
              <button onClick={handleSend} disabled={sending} className="px-4 py-2 bg-reset-terracotta text-white text-sm font-bold rounded-xl disabled:opacity-50">
                {sending ? 'Sending…' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════ AFFILIATES (master-detail) ═══════════════════════

function initials(name) {
  return (name || '').split(' ').map(p => p[0]).join('').slice(0, 2).toUpperCase();
}

function AffiliatesView() {
  const [list, setList] = useState(null);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback(() => {
    crService.getGrouped().then(rows => {
      setList(rows);
      if (!selectedId && rows.length > 0) setSelectedId(rows[0].member_id);
    }).catch(err => setError(err.response?.data?.error || 'Could not load affiliates.'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  if (error) return <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm">{error}</div>;
  if (!list) return <p className="text-sm text-gray-400">Loading…</p>;

  const filtered = list.filter(p => {
    if (search.trim() && !p.name.toLowerCase().includes(search.trim().toLowerCase())) return false;
    if (filter === 'live') return p.tags.includes('live');
    if (filter === 'pending') return p.tags.includes('pending') || p.tags.includes('incomplete');
    if (filter === 'royalty') return p.tags.includes('royalty');
    return true;
  });

  const filterCount = (key) => list.filter(p => {
    if (key === 'live') return p.tags.includes('live');
    if (key === 'pending') return p.tags.includes('pending') || p.tags.includes('incomplete');
    if (key === 'royalty') return p.tags.includes('royalty');
    return true;
  }).length;

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="font-serif text-xl text-reset-brownDeep">Manage affiliates</h2>
      </div>
      <div className="flex bg-white rounded-2xl shadow-sm overflow-hidden min-h-[600px]">
        <div className="w-72 shrink-0 border-r border-reset-creamDark flex flex-col">
          <div className="p-3 border-b border-reset-creamDark">
            <input
              value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search affiliates…"
              className="w-full px-3.5 py-2 rounded-full border border-gray-200 text-sm mb-2.5"
            />
            <div className="flex gap-1.5 flex-wrap">
              {[{ k: 'all', l: 'All' }, { k: 'live', l: 'Live' }, { k: 'pending', l: 'Pending' }, { k: 'royalty', l: 'Royalty' }].map(f => (
                <button key={f.k} onClick={() => setFilter(f.k)}
                  className={`text-xs px-3 py-1 rounded-full ${filter === f.k ? 'bg-reset-brownDeep text-white' : 'bg-reset-cream text-reset-brown'}`}>
                  {f.l} ({filterCount(f.k)})
                </button>
              ))}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-2 max-h-[600px]">
            {filtered.map(p => (
              <div key={p.member_id} onClick={() => setSelectedId(p.member_id)}
                className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer mb-1 ${selectedId === p.member_id ? 'bg-reset-terracotta/10' : 'hover:bg-reset-cream'}`}>
                <div className={`w-9 h-9 rounded-full flex items-center justify-center font-serif text-sm shrink-0 ${selectedId === p.member_id ? 'bg-reset-terracotta text-white' : 'bg-reset-terracotta/10 text-reset-terracotta'}`}>
                  {initials(p.name)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-bold text-reset-brownDeep truncate">{p.name}</div>
                  <div className="flex gap-1 mt-0.5 flex-wrap">
                    {p.tags.includes('live') && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-reset-sage/10 text-reset-sage">Live</span>}
                    {p.tags.includes('pending') && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-reset-gold/25 text-[#8a6a1f]">Pending review</span>}
                    {p.tags.includes('incomplete') && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-reset-terracotta/10 text-reset-terracotta">Incomplete</span>}
                    {p.tags.includes('royalty') && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-reset-sage/15 text-reset-sage">+ Royalty</span>}
                  </div>
                </div>
                <div className="font-serif text-sm text-reset-brownDeep shrink-0">${Number(p.owed).toFixed(2)}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="flex-1 p-7 overflow-y-auto max-h-[640px]">
          {selectedId && <AffiliateDetail memberId={selectedId} onChanged={load} />}
        </div>
      </div>
    </div>
  );
}

function AffiliateDetail({ memberId, onChanged }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [showPayout, setShowPayout] = useState(false);
  const [compose, setCompose] = useState(null);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [approving, setApproving] = useState(false);

  const load = useCallback(() => {
    setData(null);
    crService.getByMember(memberId).then(setData).catch(err => setError(err.response?.data?.error || 'Could not load this affiliate.'));
  }, [memberId]);

  useEffect(() => { load(); }, [load]);

  if (error) return <p className="text-sm text-red-600">{error}</p>;
  if (!data) return <p className="text-sm text-gray-400">Loading…</p>;

  const isRoyalty = !!data.royalty;
  const a = data.affiliate;

  const handleApprove = async () => {
    if (!window.confirm(`Go live for ${data.name}? Their link, $1 offer, and real landing page will all become active immediately.`)) return;
    setApproving(true);
    try {
      const affiliateAdminSvc = await import('../../services/adminAffiliate.service');
      await affiliateAdminSvc.approveLandingPage(a.id);
      load();
      onChanged?.();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not approve this affiliate.');
    } finally {
      setApproving(false);
    }
  };

  const handleUnapprove = async () => {
    if (!window.confirm(`Take ${data.name}'s link, $1 offer, and landing page offline?`)) return;
    setApproving(true);
    try {
      const affiliateAdminSvc = await import('../../services/adminAffiliate.service');
      await affiliateAdminSvc.unapproveLandingPage(a.id);
      load();
      onChanged?.();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not take this affiliate offline.');
    } finally {
      setApproving(false);
    }
  };

  const openCompose = async (type) => {
    setSendResult(null);
    if (type === 'email') {
      setCompose({ kind: 'custom', title: 'Send email', subject: '', body: '', headerStyle: 'jenn_photo' });
      return;
    }
    const affiliateAdminSvc = await import('../../services/adminAffiliate.service');
    const preview = type === 'intake'
      ? await affiliateAdminSvc.getIntakeEmailPreview(a.id)
      : null;
    if (type === 'intake') {
      setCompose({ kind: 'intake', title: 'Send intake email', subject: preview.subject, body: preview.body, headerStyle: 'jenn_photo', partnerId: a.id });
    } else {
      setCompose({
        kind: 'intake', title: 'Request new landing page', partnerId: a.id, headerStyle: 'jenn_photo',
        subject: 'Time for a fresh 365reset landing page',
        body: `Hi — we're refreshing affiliate landing pages. When you have a few minutes, please fill out the form in this email with an updated photo, story, and quote, and we'll have your new page live shortly.`,
      });
    }
  };

  const handleSend = async () => {
    setSending(true);
    try {
      const affiliateAdminSvc = await import('../../services/adminAffiliate.service');
      if (compose.kind === 'custom') {
        await crService.bulkEmail({ segment: 'all', type: 'custom', subject: compose.subject, body: compose.body, headerStyle: compose.headerStyle });
        // Custom single-recipient send for one person specifically:
      } else {
        await affiliateAdminSvc.sendIntakeEmail(compose.partnerId, { subject: compose.subject, body: compose.body, headerStyle: compose.headerStyle });
      }
      setSendResult({ ok: true, message: 'Sent.' });
      setCompose(null);
    } catch (err) {
      setSendResult({ ok: false, message: err.response?.data?.error || 'Could not send this.' });
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-start mb-5 flex-wrap gap-4">
        <div className="flex items-center gap-3.5">
          <div className="w-12 h-12 rounded-full bg-reset-terracotta/10 text-reset-terracotta flex items-center justify-center font-serif text-lg shrink-0">{initials(data.name)}</div>
          <div>
            <h2 className="font-serif text-2xl text-reset-brownDeep">{data.name}</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {data.email}
              {isRoyalty && <span className="ml-2 text-[10px] font-bold px-2 py-0.5 rounded-full bg-reset-sage/15 text-reset-sage align-middle">Royalty + Affiliate</span>}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowPayout(true)} className="px-4 py-2 bg-reset-terracotta text-white text-sm font-medium rounded-xl">Record payout</button>
        </div>
      </div>

      {a && (
        <div className="bg-reset-brownDeep text-reset-cream rounded-2xl px-5 py-4 flex justify-between items-center gap-4 flex-wrap mb-5">
          <div>
            <h3 className="text-base font-bold">See exactly what {data.name.split(' ')[0]} sees</h3>
            <p className="text-xs text-reset-cream/70 mt-0.5 max-w-md">Opens their live affiliate dashboard — the same numbers, the same link.</p>
          </div>
          <a href={`/admin/affiliates/${a.id}/preview`} target="_blank" rel="noopener noreferrer" className="px-4 py-2 bg-reset-gold text-reset-brownDeep text-sm font-bold rounded-full shrink-0">Preview →</a>
        </div>
      )}

      {sendResult && <div className={`text-sm px-3 py-2 rounded-lg mb-4 ${sendResult.ok ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>{sendResult.message}</div>}

      {isRoyalty ? (
        <section className="mb-6">
          <h3 className="font-serif text-lg text-reset-brownDeep mb-2">What she's owed</h3>
          <div className="bg-reset-cream rounded-2xl p-5">
            <div className="flex justify-between py-2 border-b border-reset-creamDark">
              <span className="text-sm">Royalty override <small className="block text-gray-400 text-xs">10% on every team payment</small></span>
              <span className="font-serif text-lg">${Number(data.royalty.owed).toFixed(2)}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-reset-creamDark">
              <span className="text-sm">Affiliate commission <small className="block text-gray-400 text-xs">10% on members she's referred</small></span>
              <span className="font-serif text-lg">${Number(a?.owed || 0).toFixed(2)}</span>
            </div>
            <div className="flex justify-between pt-3 mt-1 border-t-2 border-reset-brownDeep">
              <span className="text-sm font-bold">Total owed</span>
              <span className="font-serif text-xl text-reset-terracotta">${Number(data.owed).toFixed(2)}</span>
            </div>
          </div>
        </section>
      ) : (
        <div className="grid grid-cols-3 gap-3 mb-6">
          <div className="bg-reset-cream rounded-xl p-4"><p className="text-xs font-bold text-reset-sage mb-1">Owed</p><p className="font-serif text-xl text-reset-brownDeep">${Number(data.owed).toFixed(2)}</p></div>
          <div className="bg-reset-cream rounded-xl p-4"><p className="text-xs font-bold text-reset-sage mb-1">Paid to date</p><p className="font-serif text-xl text-reset-brownDeep">${Number(data.paid).toFixed(2)}</p></div>
          <div className="bg-reset-cream rounded-xl p-4"><p className="text-xs font-bold text-reset-sage mb-1">Referrals</p><p className="font-serif text-xl text-reset-brownDeep">{data.referral_count ?? 0}</p></div>
        </div>
      )}

      {a && (
        <div className="mb-6">
          <div className={`inline-flex items-center gap-2 text-xs font-medium px-3 py-1.5 rounded-full mb-2 ${
            a.link_active ? 'bg-reset-sage/10 text-reset-sage' : a.awaiting_approval ? 'bg-reset-terracotta/10 text-reset-terracotta' : 'bg-reset-gold/25 text-reset-brown'
          }`}>
            {a.link_active ? '● Live' : a.awaiting_approval ? '● Waiting on Team365 approval' : '○ Waiting on their intake form'}
          </div>
          <div>
            {a.profile_submitted_at && (
              a.link_active ? (
                <button onClick={handleUnapprove} disabled={approving} className="px-3 py-1.5 border border-gray-200 text-xs font-medium rounded-lg text-gray-600 disabled:opacity-50">{approving ? 'Working…' : 'Take offline'}</button>
              ) : (
                <button onClick={handleApprove} disabled={approving} className="px-3 py-1.5 bg-reset-sage text-white text-xs font-medium rounded-lg disabled:opacity-50">{approving ? 'Working…' : 'Approve — go live'}</button>
              )
            )}
          </div>
        </div>
      )}

      <section className="mb-6">
        <h3 className="font-serif text-lg text-reset-brownDeep mb-2">Communicate</h3>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => openCompose('email')} className="px-4 py-2 bg-reset-terracotta text-white text-sm font-medium rounded-xl">Send email</button>
          {a && <button onClick={() => openCompose('intake')} className="px-4 py-2 border border-gray-200 text-sm font-medium rounded-xl text-reset-brown">Send intake email</button>}
          {a && <button onClick={() => openCompose('landing_refresh')} className="px-4 py-2 border border-gray-200 text-sm font-medium rounded-xl text-reset-brown">Request new landing page</button>}
        </div>
      </section>

      {a && (
        <section className="mb-6">
          <h3 className="font-serif text-lg text-reset-brownDeep mb-2">Landing page</h3>
          {a.profile_submitted_at ? (
            <div className="bg-reset-cream rounded-xl overflow-hidden max-w-xs">
              <div className="aspect-video bg-gradient-to-br from-reset-terracotta/10 to-reset-sage/10 flex items-center justify-center text-reset-terracotta font-serif italic text-sm text-center p-3">
                {data.name.split(' ')[0]}'s landing page
              </div>
              <div className="p-3">
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${a.link_active ? 'bg-reset-sage/10 text-reset-sage' : 'bg-reset-gold/25 text-[#8a6a1f]'}`}>
                  {a.link_active ? 'Live' : 'Pending review'}
                </span>
                <p className="text-[11px] text-gray-400 mt-1.5">{a.profile_submitted_at ? new Date(a.profile_submitted_at).toLocaleDateString() : ''}</p>
                <div className="flex gap-1.5 mt-2">
                  {a.landing_page_preview_url && <a href={a.landing_page_preview_url} target="_blank" rel="noopener noreferrer" className="px-3 py-1 bg-reset-terracotta text-white text-xs font-bold rounded-full">{a.link_active ? 'View' : 'Review'}</a>}
                  {a.confirmation_page_preview_url && <a href={a.confirmation_page_preview_url} target="_blank" rel="noopener noreferrer" className="px-3 py-1 border border-gray-200 text-xs font-bold rounded-full">Confirmation page</a>}
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-reset-cream rounded-xl p-4 text-sm text-gray-400">No landing page submitted yet.</div>
          )}
        </section>
      )}

      <section>
        <h3 className="font-serif text-lg text-reset-brownDeep mb-2">Referred members</h3>
        {data.referred_members?.length > 0 ? (
          <div className="bg-reset-cream rounded-xl overflow-hidden">
            {data.referred_members.map(m => (
              <div key={m.id} className="flex justify-between items-center px-4 py-2.5 border-b border-white last:border-0 text-sm">
                <div><div className="font-bold text-reset-brownDeep">{m.name}</div><div className="text-xs text-gray-400">Joined {m.member_since ? new Date(m.member_since).toLocaleDateString() : '—'}</div></div>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-reset-sage/15 text-reset-sage">{m.status_label}</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-reset-cream rounded-xl p-4 text-sm text-gray-400">No one yet — fills in as soon as their link brings in a real member.</div>
        )}
      </section>

      {showPayout && a && (
        <PayoutModal partnerId={a.id} name={data.name} owed={data.owed} onClose={() => setShowPayout(false)} onRecorded={() => { setShowPayout(false); load(); onChanged?.(); }} />
      )}

      {compose && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={() => setCompose(null)}>
          <div className="bg-white rounded-2xl shadow-lg max-w-lg w-full p-6" onClick={e => e.stopPropagation()}>
            <h3 className="font-serif text-lg text-reset-brownDeep mb-4">{compose.title}</h3>
            <div className="mb-3">
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">Subject</label>
              <input value={compose.subject} onChange={e => setCompose({ ...compose, subject: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div className="mb-4">
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">Message</label>
              <RichTextEditor value={compose.body} onChange={(body) => setCompose({ ...compose, body })} minHeightClass="min-h-[140px]" />
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => setCompose(null)} className="text-sm text-gray-500">Cancel</button>
              <button onClick={handleSend} disabled={sending} className="px-4 py-2 bg-reset-terracotta text-white text-sm font-bold rounded-xl disabled:opacity-50">{sending ? 'Sending…' : 'Send'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PayoutModal({ partnerId, name, owed, onClose, onRecorded }) {
  const [amount, setAmount] = useState(Number(owed).toFixed(2));
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const affiliateAdminSvc = await import('../../services/adminAffiliate.service');
      await affiliateAdminSvc.recordPayout(partnerId, { amount: Number(amount), note: note.trim() || null });
      onRecorded();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not record this payout.');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center p-4 z-50">
      <form onSubmit={submit} className="bg-white rounded-2xl shadow-lg p-5 w-full max-w-sm space-y-3">
        <h3 className="font-serif text-lg text-reset-brownDeep">Record payout — {name}</h3>
        {error && <p className="text-xs text-red-600">{error}</p>}
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Amount</label>
          <input type="number" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} required className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Note (optional)</label>
          <input value={note} onChange={e => setNote(e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
        </div>
        <div className="flex justify-end gap-3 pt-1">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-500">Cancel</button>
          <button type="submit" disabled={saving} className="px-4 py-2 bg-reset-terracotta text-white text-sm font-medium rounded-xl disabled:opacity-50">{saving ? 'Saving…' : 'Record payout'}</button>
        </div>
      </form>
    </div>
  );
}

// ═══════════════════════ CONTENT LIBRARY ═══════════════════════

const LIBRARY_TABS = [
  { key: 'templates', label: 'Landing page templates' },
  { key: 'instagram', label: 'Instagram & Threads' },
  { key: 'facebook', label: 'Facebook' },
  { key: 'linkedin', label: 'LinkedIn' },
  { key: 'tiktok', label: 'TikTok' },
  { key: 'pinterest', label: 'Pinterest' },
  { key: 'email', label: 'Email & newsletter' },
];

// Jenn, 2026-09-12: "there is no reason why that isn't built. you already
// built that in the back end. This just puts it somewhere more accessible
// to me." The real affiliate_media upload/assign system (migration 083)
// already existed — this is that same real system, just reachable from
// here, now genuinely tagged per platform (migration 115) instead of one
// flat list, so a real Instagram-shaped image and a real Pinterest-shaped
// one don't get mixed together.
function LibraryView() {
  const [tab, setTab] = useState('templates');
  return (
    <div>
      <h2 className="font-serif text-xl text-reset-brownDeep mb-1">Content Library</h2>
      <p className="text-sm text-gray-500 mb-6">Managed by you — what you add here is what affiliates see on their dashboards</p>

      <div className="flex gap-2 flex-wrap mb-5">
        {LIBRARY_TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-4 py-2 rounded-full text-sm font-bold ${tab === t.key ? 'bg-reset-terracotta text-white' : 'bg-white border border-gray-200 text-reset-brown'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'templates' ? <TemplatesTab /> : <PlatformMediaTab platform={tab} label={LIBRARY_TABS.find(t => t.key === tab).label} />}
    </div>
  );
}

function TemplatesTab() {
  return (
    <div>
      <div className="grid sm:grid-cols-3 gap-4 mb-5">
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <div className="aspect-video bg-gradient-to-br from-reset-terracotta/10 to-reset-gold/25 flex items-center justify-center text-reset-terracotta font-serif italic text-sm text-center p-3">
            Story-led template
          </div>
          <div className="p-3">
            <p className="text-sm font-bold text-reset-brownDeep">Story-led (current default)</p>
            <p className="text-xs text-gray-400 mt-0.5">Used by every affiliate right now</p>
          </div>
        </div>
      </div>
      <InDevelopmentCard
        title="Multiple assignable templates"
        sub="Today every affiliate's real landing page renders from that one template above. Building a second or third template someone could choose per-affiliate is real, unbuilt work — a to-do has been added for it."
      />
    </div>
  );
}

function PlatformMediaTab({ platform, label }) {
  const [media, setMedia] = useState(null);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [title, setTitle] = useState('');
  const [dragging, setDragging] = useState(false);
  const fileInputRef = React.useRef(null);

  const load = useCallback(() => {
    affiliateAdminService.listMedia(null, platform).then(setMedia).catch(() => setError('Could not load media.'));
  }, [platform]);

  useEffect(() => { load(); }, [load]);

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    setUploading(true);
    setError(null);
    try {
      for (const file of files) {
        const url = await uploadImage(file);
        await affiliateAdminService.createMedia({
          partnerId: null, // available to every affiliate — per-affiliate assignment stays on the affiliate's own detail page
          title: files.length === 1 ? (title.trim() || file.name) : file.name,
          fileUrl: url,
          platform,
        });
      }
      setTitle('');
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not upload one of these files.');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this file from affiliate dashboards?')) return;
    await affiliateAdminService.deleteMedia(id);
    load();
  };

  return (
    <div>
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); handleFiles(e.dataTransfer.files); }}
        className={`bg-white rounded-2xl shadow-sm p-6 mb-5 text-center border-2 border-dashed transition-colors ${dragging ? 'border-reset-terracotta bg-reset-terracotta/5' : 'border-reset-terracotta/30'}`}
      >
        <p className="text-sm font-bold text-reset-brownDeep mb-1">Drag {label.toLowerCase()} assets here</p>
        <input
          value={title} onChange={e => setTitle(e.target.value)}
          placeholder="File title (optional — only used for a single file)"
          className="w-full max-w-xs mx-auto block border border-gray-200 rounded-full px-3 py-1.5 text-xs mb-3 focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
        />
        <p className="text-xs text-gray-400 mb-3">or</p>
        <input ref={fileInputRef} type="file" multiple onChange={(e) => { handleFiles(e.target.files); e.target.value = ''; }} className="hidden" />
        <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploading}
          className="px-4 py-2 bg-reset-terracotta text-white text-sm font-bold rounded-full disabled:opacity-50">
          {uploading ? 'Uploading…' : 'Choose files'}
        </button>
      </div>

      {error && <p className="text-sm text-red-600 mb-3">{error}</p>}

      {!media ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : media.length === 0 ? (
        <p className="text-sm text-gray-400">No {label.toLowerCase()} files yet.</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {media.map(m => (
            <div key={m.id} className="bg-white rounded-2xl shadow-sm overflow-hidden">
              <a href={m.file_url} target="_blank" rel="noopener noreferrer" className="block aspect-square bg-reset-cream">
                <img src={m.file_url} alt={m.title} className="w-full h-full object-cover" />
              </a>
              <div className="p-2.5">
                <p className="text-xs text-gray-500 truncate mb-1.5">{m.title}</p>
                <button onClick={() => handleDelete(m.id)} className="text-xs text-red-400 hover:text-red-600">Remove</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
